import * as React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Clock } from "lucide-react";

import { cn } from "@/lib/utils";
import { ActivityRunTime } from "./ActivityRunTime";
import type { TaskActivity } from "../types";

interface ActivityListProps {
  activities: TaskActivity[];
}

/**
 * Clean, calm list of work activities — shared by the task expanded row and the
 * adjustment cards so both read identically. Each row: a status accent bar, a
 * duration pill (live for running timers), the description + type, and the date.
 */
export function ActivityList({ activities }: ActivityListProps) {
  const { t } = useTranslation();

  // Latest-created activity first (oldest last). `id` is the creation-order
  // fallback for payloads that omit `createdAt`.
  const sortedActivities = React.useMemo(
    () =>
      [...activities].sort((a, b) => {
        if (a?.createdAt && b?.createdAt) {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }
        return (b?.id ?? 0) - (a?.id ?? 0);
      }),
    [activities],
  );

  if (activities.length === 0) {
    return (
      <p className="text-muted-foreground px-4 py-6 text-center text-sm">
        {t("noActivities")}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-slate-100">
      {sortedActivities.map((activity) => {
        const isRejected = activity.status === "REJECT";
        const isRunning = !!activity.lastStartTime && !activity.lastEndTime;
        const hasTimeMismatch =
          activity.userEnteredTimeInSeconds != null &&
          activity.runTimeInSeconds != null &&
          activity.userEnteredTimeInSeconds !== activity.runTimeInSeconds;

        return (
          <li
            key={activity.id}
            className={cn(
              "flex items-center gap-3.5 px-4 py-3 transition-colors hover:bg-slate-50/80",
              isRejected && "bg-red-50/40",
            )}
          >
            {/* Status accent */}
            <span
              aria-hidden
              className={cn(
                "h-9 w-1 shrink-0 rounded-full",
                isRejected
                  ? "bg-red-400"
                  : isRunning
                    ? "bg-emerald-500"
                    : "bg-slate-200",
              )}
            />

            {/* Duration — a red border marks a manually-entered time (entered ≠ tracked) */}
            <div
              title={hasTimeMismatch ? t("timeMismatchHint") : undefined}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1",
                isRejected
                  ? "bg-red-100"
                  : isRunning
                    ? "bg-emerald-50"
                    : "bg-slate-100",
                hasTimeMismatch && "border border-red-500",
              )}
            >
              {isRunning ? (
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              ) : (
                <Clock className="text-muted-foreground h-3.5 w-3.5" />
              )}
              <ActivityRunTime activity={activity} />
            </div>

            {/* Description + type */}
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "truncate text-sm",
                  isRejected
                    ? "text-muted-foreground line-through"
                    : "text-slate-700",
                )}
              >
                {activity.description || "—"}
              </p>
              {activity.type?.name && (
                <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                  {activity.type.name}
                </span>
              )}
            </div>

            {/* Status pill + date */}
            <div className="flex shrink-0 flex-col items-end gap-1">
              {isRejected ? (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-600">
                  {t("status.rejected")}
                </span>
              ) : isRunning ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  {t("running")}
                </span>
              ) : null}
              {activity.lastStartTime && (
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {format(new Date(activity.lastStartTime), "MMM d, yyyy")}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
